import { timeAgo, exerciseTitle } from "components/common/utils";
import { GetSubmissionsOutput } from "server/StartupAdmins CRUD/OutputsTypes";
import { ReferenceType, SubmissionType, Exercise, ExerciseStatus } from "types";

import { BASE_URL } from "server";

export interface TableRow {
  _id: string;
  title: string;
  status: ExerciseStatus;
  updatedAt: string;
  timestamp: string;
  observation: string;
  submissionType: SubmissionType;
  isActivated: boolean;
}

export async function fetchExercises(): Promise<TableRow[]> {
  const response = await fetch(`${BASE_URL}/problems/mine`, {
    method: "GET",
    headers: {
      Accept: "*/*",
    },
    credentials: "include",
  });

  if (!response.ok) {
    console.log(`HTTP error! status: ${response.status}`);
    return [];
  }
  let exercises = [];
  try {
    exercises = await response.json();
  } catch (e) {
    return [];
  }

  let TABLE_ROWS = exercises.map((exercise: GetSubmissionsOutput) => {
    const title = exerciseTitle(exercise);

    return {
      _id: exercise._id,
      isActivated: exercise.isActivated,
      title,
      status: exercise.status,
      updatedAt: timeAgo(new Date(exercise.updatedAt)),
      timestamp: exercise.updatedAt,
      observation: exercise.observation,
      submissionType: exercise.submissionType,
    };
  });

  // Trier les exercices par la date de dernière mise à jour
  TABLE_ROWS = TABLE_ROWS.sort(
    (a: TableRow, b: TableRow) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  return TABLE_ROWS;
}

// Fonction de validation commune pour tous les types de soumission
export const validateSubmissionFields = (
  submissionType: string,
  step: number,
  state: any,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Validation selon le type de soumission et l'étape
  switch (submissionType) {
    case "control":
      return validateControlSubmission(step, state);
    case "blocage":
      return validateBlocageSubmission(step, state);
    case "unclear":
      return validateUnclearSubmission(step, state);
    default:
      errors.push("Type de soumission invalide");
      return { isValid: false, errors };
  }
};

// Validation pour le type "control"
const validateControlSubmission = (
  step: number,
  state: any,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  switch (step) {
    case 1:
      // Étape 1: Fichiers du sujet
      if (
        !state.subjectFileIds ||
        !Array.isArray(state.subjectFileIds) ||
        state.subjectFileIds.length === 0
      ) {
        errors.push("Veuillez ajouter au moins un fichier de sujet");
      }
      break;

    case 2:
      // Étape 2: Références
      if (!state.referenceType || state.referenceType.trim() === "") {
        errors.push("Veuillez sélectionner un type de référence");
      } else {
        // Validation selon le type de référence
        switch (state.referenceType) {
          case "livre":
            if (
              !state.referenceBook ||
              !state.referenceBook.name ||
              !state.referenceBook.page ||
              !state.referenceBook.exerciseNumber
            ) {
              errors.push(
                "Veuillez remplir tous les champs de référence du livre",
              );
            }
            break;
          case "epreuve":
            if (
              !state.referenceTest ||
              !state.referenceTest.schoolName ||
              !state.referenceTest.year ||
              !state.referenceTest.session ||
              !state.referenceTest.class
            ) {
              errors.push(
                "Veuillez remplir tous les champs de référence de l'épreuve",
              );
            }
            break;
          case "other":
            if (!state.referenceOther || !state.referenceOther.info) {
              errors.push("Veuillez décrire la référence");
            }
            break;
        }
      }
      break;

    case 3:
      // Étape 3: Commentaire (optionnel pour control)
      break;

    case 4:
      // Étape 4: Fichiers de production
      if (
        !state.productionFileIds ||
        !Array.isArray(state.productionFileIds) ||
        state.productionFileIds.length === 0
      ) {
        errors.push("Veuillez ajouter au moins un fichier de production");
      }
      break;
  }

  return { isValid: errors.length === 0, errors };
};

// Validation pour le type "blocage"
const validateBlocageSubmission = (
  step: number,
  state: any,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  switch (step) {
    case 1:
      // Étape 1: Fichiers du sujet
      if (
        !state.subjectFileIds ||
        !Array.isArray(state.subjectFileIds) ||
        state.subjectFileIds.length === 0
      ) {
        errors.push("Veuillez ajouter au moins un fichier de sujet");
      }
      break;

    case 2:
      // Étape 2: Références
      if (!state.referenceType || state.referenceType.trim() === "") {
        errors.push("Veuillez sélectionner un type de référence");
      } else {
        // Validation selon le type de référence
        switch (state.referenceType) {
          case "livre":
            if (
              !state.referenceBook ||
              !state.referenceBook.name ||
              !state.referenceBook.page ||
              !state.referenceBook.exerciseNumber
            ) {
              errors.push(
                "Veuillez remplir tous les champs de référence du livre",
              );
            }
            break;
          case "epreuve":
            if (
              !state.referenceTest ||
              !state.referenceTest.schoolName ||
              !state.referenceTest.year ||
              !state.referenceTest.session ||
              !state.referenceTest.class
            ) {
              errors.push(
                "Veuillez remplir tous les champs de référence de l'épreuve",
              );
            }
            break;
          case "other":
            if (!state.referenceOther || !state.referenceOther.info) {
              errors.push("Veuillez décrire la référence");
            }
            break;
        }
      }
      break;

    case 3:
      // Étape 3: Commentaire (obligatoire pour blocage)
      if (!state.comment || state.comment.trim() === "") {
        errors.push("Veuillez décrire votre blocage");
      }
      break;

    case 4:
      // Étape 4: Fichiers de production
      if (
        !state.productionFileIds ||
        !Array.isArray(state.productionFileIds) ||
        state.productionFileIds.length === 0
      ) {
        errors.push("Veuillez ajouter au moins un fichier de production");
      }
      break;
  }

  return { isValid: errors.length === 0, errors };
};

// Validation pour le type "unclear"
const validateUnclearSubmission = (
  step: number,
  state: any,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  switch (step) {
    case 1:
      // Étape 1: Fichiers du sujet
      if (
        !state.subjectFileIds ||
        !Array.isArray(state.subjectFileIds) ||
        state.subjectFileIds.length === 0
      ) {
        errors.push("Veuillez ajouter au moins un fichier de sujet");
      }
      break;

    case 2:
      // Étape 2: Références
      if (!state.referenceType || state.referenceType.trim() === "") {
        errors.push("Veuillez sélectionner un type de référence");
      } else {
        // Validation selon le type de référence
        switch (state.referenceType) {
          case "livre":
            if (
              !state.referenceBook ||
              !state.referenceBook.name ||
              !state.referenceBook.page ||
              !state.referenceBook.exerciseNumber
            ) {
              errors.push(
                "Veuillez remplir tous les champs de référence du livre",
              );
            }
            break;
          case "epreuve":
            if (
              !state.referenceTest ||
              !state.referenceTest.schoolName ||
              !state.referenceTest.year ||
              !state.referenceTest.session ||
              !state.referenceTest.class
            ) {
              errors.push(
                "Veuillez remplir tous les champs de référence de l'épreuve",
              );
            }
            break;
          case "other":
            if (!state.referenceOther || !state.referenceOther.info) {
              errors.push("Veuillez décrire la référence");
            }
            break;
        }
      }
      break;

    case 3:
      // Étape 3: Commentaire (obligatoire pour unclear)
      if (!state.comment || state.comment.trim() === "") {
        errors.push("Veuillez décrire votre problème");
      }
      break;
  }

  return { isValid: errors.length === 0, errors };
};

// Fonction pour valider la soumission complète
export const validateCompleteSubmission = (
  submissionType: string,
  state: any,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Validation de base commune à tous les types
  if (
    !state.subjectFileIds ||
    !Array.isArray(state.subjectFileIds) ||
    state.subjectFileIds.length === 0
  ) {
    errors.push("Veuillez ajouter au moins un fichier de sujet");
  }

  if (!state.referenceType || state.referenceType.trim() === "") {
    errors.push("Veuillez sélectionner un type de référence");
  } else {
    // Validation des détails de référence selon le type
    switch (state.referenceType) {
      case "livre":
        if (
          !state.referenceBook ||
          !state.referenceBook.name ||
          !state.referenceBook.page ||
          !state.referenceBook.exerciseNumber
        ) {
          errors.push("Veuillez remplir tous les champs de référence du livre");
        }
        break;
      case "epreuve":
        if (
          !state.referenceTest ||
          !state.referenceTest.schoolName ||
          !state.referenceTest.year ||
          !state.referenceTest.session ||
          !state.referenceTest.class
        ) {
          errors.push(
            "Veuillez remplir tous les champs de référence de l'épreuve",
          );
        }
        break;
      case "other":
        if (!state.referenceOther || !state.referenceOther.info) {
          errors.push("Veuillez décrire la référence");
        }
        break;
    }
  }

  // Validation spécifique selon le type
  switch (submissionType) {
    case "control":
      if (
        !state.productionFileIds ||
        !Array.isArray(state.productionFileIds) ||
        state.productionFileIds.length === 0
      ) {
        errors.push("Veuillez ajouter au moins un fichier de production");
      }
      break;

    case "blocage":
      if (!state.comment || state.comment.trim() === "") {
        errors.push("Veuillez décrire votre blocage");
      }
      if (
        !state.productionFileIds ||
        !Array.isArray(state.productionFileIds) ||
        state.productionFileIds.length === 0
      ) {
        errors.push("Veuillez ajouter au moins un fichier de production");
      }
      break;

    case "unclear":
      if (!state.comment || state.comment.trim() === "") {
        errors.push("Veuillez décrire votre problème");
      }
      break;
  }

  return { isValid: errors.length === 0, errors };
};
